
import { formatDate, getIsoDateStringAsDate, getStartDateFromDateRange, getUkCalendarDateStringFromDate } from '../date-time.js';

export const graphDataMapKeys = { OVERALL: 1, VISITS: 2, WORK: 3, PHONE: 4, MEALS: 5, CANTEEN: 6, GYM: 7, ACTIVITIES: 8, SURVEY_HISTORY: 9, BALANCE_HISTORY: 10 };

export function createGraphDataMap(yearOnYear, history, surveyHistory, balanceHistory, futureDays) {
    if (yearOnYear) {
        return createYearOnYearGraphDataMap(history);
    } else {
        return createDateRangeGraphDataMap(history, surveyHistory, balanceHistory, futureDays);
    }
}

function createDateRangeGraphDataMap(history, surveyHistory, balanceHistory, futureDays) {
    var visitScores = [], workScores = [], phoneScores = [], mealScores = [], canteenScores = [], gymScores = [],
        activityScores = [], overallScores = [], surveyScores = [], balanceScores = [];

    // Create a map of all dates between the first and last dates in the history
    let isolationScoreMap = getDateMap(getIsoDateStringAsDate(history[history.length - 1].date), getIsoDateStringAsDate(history[0].date));

    // Iterate through the history and for each date where scores exists, add them to the date map
    history.forEach((item) => {
        let formattedDate = formatDate(item.date);
        isolationScoreMap.set(formattedDate, {
            levelVisit: (item.levelVisit ? 4 - item.levelVisit : null),
            levelWork: (item.levelWork ? 4 - item.levelWork : null),
            levelPhone: (item.levelPhone ? 4 - item.levelPhone : null),
            levelMeal: (item.levelMeal ? 4 - item.levelMeal : null),
            levelCanteen: (item.levelCanteen ? 4 - item.levelCanteen : null),
            levelGym: (item.levelGym ? 4 - item.levelGym : null),
            levelActivity: (item.levelActivity ? 4 - item.levelActivity : null),
            weightedPercentage: item.weightedPercentage
        });
    });

    // Iterate through the date map and create datasets for each score type
    isolationScoreMap.forEach((value, key) => {
        visitScores.push({y: value ? value.levelVisit : null, x: key});
        workScores.push({y: value ? value.levelWork : null, x: key});
        phoneScores.push({y: value ? value.levelPhone : null, x: key});
        mealScores.push({y: value ? value.levelMeal : null, x: key});
        canteenScores.push({y: value ? value.levelCanteen : null, x: key});
        gymScores.push({y: value ? value.levelGym : null, x: key});
        activityScores.push({y: value ? value.levelActivity : null, x: key});
        overallScores.push({y: value ? value.weightedPercentage : null, x: key});
    });

    // Add extra "empty" days to the end of each dataset to allow (e.g) upcoming trigger dates to display on the graph
    for (let i = 1; i <= futureDays; i++) {
        let extraDate = new Date();
        extraDate.setDate(extraDate.getDate() + i);
        visitScores.push({y : null, x : getUkCalendarDateStringFromDate(extraDate)});
        workScores.push({y : null, x : getUkCalendarDateStringFromDate(extraDate)});
        phoneScores.push({y : null, x : getUkCalendarDateStringFromDate(extraDate)});
        mealScores.push({y : null, x : getUkCalendarDateStringFromDate(extraDate)});
        canteenScores.push({y : null, x : getUkCalendarDateStringFromDate(extraDate)});
        gymScores.push({y : null, x : getUkCalendarDateStringFromDate(extraDate)});
        activityScores.push({y : null, x : getUkCalendarDateStringFromDate(extraDate)});
        overallScores.push({y : null, x : getUkCalendarDateStringFromDate(extraDate)});
    }

    // Create a map of all the datasets
    let graphData = new Map();
    graphData.set(graphDataMapKeys.OVERALL, overallScores);
    graphData.set(graphDataMapKeys.VISITS, visitScores);
    graphData.set(graphDataMapKeys.WORK, workScores);
    graphData.set(graphDataMapKeys.PHONE, phoneScores);
    graphData.set(graphDataMapKeys.MEALS, mealScores);
    graphData.set(graphDataMapKeys.CANTEEN, canteenScores);
    graphData.set(graphDataMapKeys.GYM, gymScores);
    graphData.set(graphDataMapKeys.ACTIVITIES, activityScores);

    // Convert the raw survey history data into chart dataset compatible objects and add to the map
    surveyHistory.forEach((item) => {
        surveyScores.unshift({y : 100 - item.weightedPercentage, x : formatDate(item.dateSurvey)});
    });

    graphData.set(graphDataMapKeys.SURVEY_HISTORY, surveyScores);

    // Convert the raw balance history data into chart dataset compatible objects and add to the map
    let balanceMap = getDateMap(getIsoDateStringAsDate(history[history.length - 1].date), getIsoDateStringAsDate(history[0].date));

    balanceHistory.forEach((item) => {
        balanceMap.set(formatDate(item.date), item.total);
    });

    balanceMap.forEach((value, key) => {
        balanceScores.push({y : value, x : key});
    });

    graphData.set(graphDataMapKeys.BALANCE_HISTORY, balanceScores);

    return graphData;
}

function createYearOnYearGraphDataMap(history) {
    let graphData = new Map();
    let currentYear = parseInt(formatDate(history[0].date).slice(-4));
    let scoreMap = getLeapYearDateMap();

    history.forEach((item) => {
        if (parseInt(formatDate(item.date).slice(-4)) == currentYear) {
            scoreMap.set(
                formatDate(item.date).slice(0,5),
                {y: item.weightedPercentage, x: formatDate(item.date).slice(0,5)});
            // On non-leap years, duplicate the score on 28/02 into 29/02 to bridge gaps in the line chart 
            if (formatDate(item.date).slice(0,5) == "28/02" && scoreMap.get("29/02") == null) {
                scoreMap.set(
                "29/02",
                {y: item.weightedPercentage, x: "29/02"});
            }
        } else {
            graphData.set(currentYear, scoreMap);
            scoreMap = getLeapYearDateMap();
            currentYear = parseInt(formatDate(item.date).slice(-4));
        }
    });
    graphData.set(currentYear, scoreMap);

    return graphData;
}

// Create a map of all dates between two given dates
function getDateMap(startDate, endDate) {
    var dateMap = new Map();
    while (startDate < endDate) {
        dateMap.set(getUkCalendarDateStringFromDate(new Date(startDate)), null);
        startDate.setDate(startDate.getDate() + 1)
    }
    return dateMap;
}

// Create a map of all dates in a leap year
function getLeapYearDateMap() {
    var date = new Date(2012, 0, 1);
    var end =  new Date(date);
    end.setFullYear(end.getFullYear() + 1);
    var dateMap = new Map();
    while (date < end) {
        dateMap.set(getUkCalendarDateStringFromDate(new Date(date)).slice(0,5), null);
        date.setDate(date.getDate() + 1)
    }
    return dateMap;
}

const LATEST_STAY = -3;
export function getStartDate(selectedDateRange, selectedInmateDateArrived) {
    if (selectedDateRange == LATEST_STAY) {
        return getIsoDateStringAsDate(selectedInmateDateArrived);
    } else {
        return getStartDateFromDateRange(selectedDateRange);
    }
}

export function getFinishDate(selectedInmateDateDeparted) {
    if ((selectedInmateDateDeparted) && (getIsoDateStringAsDate(selectedInmateDateDeparted) > getStartDate())) {    //if already departed finish date could be before the graph start date
        return getIsoDateStringAsDate(selectedInmateDateDeparted);
    } else {
        return new Date();
    }
}

export function createOverallChartDatasets(yearOnYear, graphData, datasetActiveStatus, isSurveyDataDisplayed, isBalanceDataDisplayed, surveyLabelName) {
    let datasets = [];
    
    if (yearOnYear) {
        let dataset = [], i = 0;
        graphData.forEach((v, k) => {
            v.forEach((value, key) => {
                if (value) {
                    dataset.push(value);
                } else {
                    dataset.push({y : null, x : key});
                }
            });
            datasets.push(createChartDataSet(i, dataset, k, datasetActiveStatus[i]));
            dataset = [];
            i++;
        });
    } else {
        datasets.push(createChartDataSet(0, graphData.get(graphDataMapKeys.OVERALL), 'Isolation', false));
        if (isSurveyDataDisplayed) {
            datasets.push(createChartDataSet(1, graphData.get(graphDataMapKeys.SURVEY_HISTORY), surveyLabelName, false));
        }
        if (isBalanceDataDisplayed) {
            datasets.push(createChartDataSet(2, graphData.get(graphDataMapKeys.BALANCE_HISTORY), 'Balance', false, 'y1'));
        }
    }

    return datasets;
}

const datasetColours = ['rgb(62, 29, 96)', 'rgb(0, 131, 107)','rgb(194, 24, 91)', 'rgb(25, 118, 210)', 'rgb(255, 143, 0)'];

function createChartDataSet(colourIndex, dataset, label, hidden, axisID) {
    return {
        label: label,
        data: dataset,
        clip: 4,
        borderColor: datasetColours[colourIndex],
        pointBorderWidth: 0.5,
        pointRadius: 0.75,
        pointHoverRadius: 2,
        fill: false,
        tension: 0.1,
        borderWidth: 2,
        hidden: hidden,
        yAxisID: axisID ? axisID : 'y' 
    }
}

export function createIndividualChartDatasets(graphData, selectedGraph, isBalanceDataDisplayed) {
    let datasets = [];
    
    datasets.push({
        data: graphData.get(selectedGraph),
        yAxisID: 'y',
        clip: 4,
        fill: false,
        tension: 0.1,
        pointBorderWidth: 1.25,
        pointRadius: 1,
        pointHoverRadius: 3.25,
        borderWidth: 2.75,
        borderColor: function (context) {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) {
                return null;
            }
            return getGradient(ctx, chartArea);
        }
    });

    if (isBalanceDataDisplayed) {
        datasets.push(createChartDataSet(2, graphData.get(graphDataMapKeys.BALANCE_HISTORY), 'Balance', false, 'y1'));
    }

    return datasets;
}

function getGradient(ctx, chartArea) {
    let gradient, width, height;
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (gradient === null || width !== chartWidth || height !== chartHeight) {
        width = chartWidth;
        height = chartHeight;
        gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        gradient.addColorStop(0, 'rgb(40, 167, 69)');
        gradient.addColorStop(0.33, 'rgb(192, 192, 192)');
        gradient.addColorStop(0.66, 'rgb(255, 193, 7)');
        gradient.addColorStop(1, 'rgb(220, 53, 69)');
    }
    return gradient;
}